import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeFrCa from '@angular/common/locales/fr-CA';
import { Inject, NgModule, Optional, SecurityContext, provideZonelessChangeDetection } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { LexiconModule } from '@galaxy/lexicon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyI18NModule } from '@vendasta/galaxy/i18n';
import { GalaxyNavModule } from '@vendasta/galaxy/nav';
import { provideChunkLoadRetry, provideErrorTracking } from '@vendasta/core';
import { GalaxyDefaultProviderOverrides } from '@vendasta/galaxy/provider-default-overrides';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { MarkdownModule, MARKED_OPTIONS, MarkedRenderer, MarkedOptions } from 'ngx-markdown';
import { Observable, of } from 'rxjs';
import { map, publishReplay, refCount, startWith } from 'rxjs/operators';
import En from '../assets/i18n/en_devel.json';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { ObservatoryHomeComponent } from './pages/home/home.component';
import { SharedModule } from './shared/shared.module';
import { SiteSearchComponent } from './shared/site-search/site-search.component';

registerLocaleData(localeFrCa);

// function that returns `MarkedOptions` with renderer override
export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();

  /* prettier-ignore */
  renderer.heading = (text: string, level: number) => {
    // This converts h* tags
    const escapedText = text.toLowerCase().replace(/[^\w]+/g, '-');
    return `
    <h${level}>
      <a
        name="${escapedText}" class="anchor"
        href="${window.location.pathname + window.location.search}#${escapedText}"
        onmouseover="this.href = window.location.pathname + window.location.search + '#${escapedText}'"
      >
        <span class="octicon octicon-link"></span>
      </a>
      ${text}
    </h${level}>`;

  };

  return {
    renderer: renderer,
    gfm: true,
    breaks: false,
    pedantic: false,
  };
}

@NgModule({
  declarations: [AppComponent, ObservatoryHomeComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    RouterModule,
    AppRoutingModule,
    HighlightModule,
    SharedModule,
    MatIconModule,
    MatTableModule,
    MatNativeDateModule,
    MatDialogModule, // required for OpenConfirmationModalService
    MatButtonModule,
    GalaxyNavModule,
    SiteSearchComponent,
    MarkdownModule.forRoot({
      loader: HttpClient,
      sanitize: SecurityContext.NONE,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    ProductAnalyticsModule,
    GalaxyI18NModule,
    TranslateModule,
    LexiconModule.forRoot({
      componentName: 'common/observatory',
      baseTranslation: En,
    }),
    CommonModule,
    GalaxyBadgeModule,
  ],
  providers: [
    provideErrorTracking(),
    provideChunkLoadRetry(),
    provideZonelessChangeDetection(),
    ...GalaxyDefaultProviderOverrides,
    {
      provide: 'CURRENT_LANG',
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => {
        return translateService.onLangChange.pipe(
          map((langChange) => langChange.lang),
          startWith(translateService.currentLang),
        );
      },
    },
    {
      provide: 'DEFAULT_LANG',
      useFactory: () => of('en').pipe(publishReplay(1), refCount()),
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        fullLibraryLoader: () => import('highlight.js'),
      },
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(
    private translateService: TranslateService,
    @Optional() @Inject('CURRENT_LANG') currentLang$: Observable<string>,
    @Optional() @Inject('DEFAULT_LANG') defaultLang$: Observable<string>,
  ) {
    if (currentLang$) {
      currentLang$.subscribe((currentLang) => {
        return translateService.use(currentLang);
      });
    }
    if (defaultLang$) {
      defaultLang$.subscribe((defaultLang) => translateService.setDefaultLang(defaultLang));
    }
  }
}
